import React from "react";
import { useTranslation } from "react-i18next";

import { Can } from "@/casl.config";
import { SaleOrder } from "@/types/general";
import { formatPriceWithCurrency, normalizePrice } from "@helpers/utils";
import { useAppSelector } from "@redux/hooks";

/**
 * Internal contribution margin (dekningsbidrag) panel, rendered below the order totals.
 *
 * Used both by the order editor, which computes the summary from the current form lines, and by
 * the saved order view, which takes it from the backend. Taking a prepared summary is what lets
 * the two stay identical.
 *
 * Gating lives inside the component on purpose: the editor has no other permission checks, so a
 * gate left to the call sites would be one omission away from exposing cost prices.
 */
export default function ContributionMarginDetails({
    summary,
}: {
    summary: SaleOrder.Components.ContributionMarginSummary;
}) {
    const { t } = useTranslation("", { keyPrefix: "sales.orders.general.orders.details" });

    const baseCurrency = useAppSelector((state) => state.auth.user?.company?.currency);

    return (
        <Can I="price.view" a="sale_order">
            <div className="flex justify-end mt-[24px] font-thin">
                <div className="w-1/2 grid grid-cols-2 gap-y-[16px]">
                    {/* Section header: shares the value column's right edge (pr-[16px]) so it
                        lines up with the figures below instead of overhanging them.
                        The rule is set inline because Tailwind's border utilities currently
                        paint all four sides here. */}
                    <div
                        className="col-span-2 pt-[24px] pr-[16px] text-right text-accent font-semibold"
                        style={{
                            borderTop: "1px solid #D9D6DA",
                            borderRight: "none",
                            borderBottom: "none",
                            borderLeft: "none",
                        }}
                    >
                        {t("heading")}
                    </div>

                    <div className="text-right">{t("listPrice")}</div>
                    <div className="text-right pr-[16px]">
                        {formatPriceWithCurrency(summary.list_price_total, baseCurrency)}
                    </div>

                    <div className="text-right">{t("extraCost")}</div>
                    <div className="text-right pr-[16px]">
                        {formatPriceWithCurrency(summary.indirect_cost_total, baseCurrency)}
                    </div>

                    <div className="text-right">{t("discount")}</div>
                    <div className="text-right pr-[16px]">
                        {formatPriceWithCurrency(summary.discount_total, baseCurrency)}
                    </div>

                    <div className="text-right">{t("marginPercentage")}</div>
                    <div className="text-right pr-[16px]">
                        {`${normalizePrice(summary.contribution_margin_percentage)}%`}
                    </div>

                    <div className="text-right font-semibold text-accent">
                        {t("contributionMargin")}
                    </div>
                    <div className="text-right pr-[16px] font-semibold text-accent">
                        {formatPriceWithCurrency(summary.contribution_margin, baseCurrency)}
                    </div>

                    <div className="text-right">{t("lineItems")}</div>
                    <div className="text-right pr-[16px]">{summary.line_count}</div>
                </div>
            </div>
        </Can>
    );
}
